<template>
  <div class="signup-page">
    <h2 class="signup-title">会員登録</h2>
    <div class="form">
      <v-form v-if="!isConfirming" ref="form">
        <FormLabelTemplate class="form-item" label="メールアドレス">
          <text-field v-model="email" required type="email" />
        </FormLabelTemplate>
        <FormLabelTemplate class="form-item" label="パスワード">
          <text-field v-model="password" required type="password" />
        </FormLabelTemplate>
        <FormLabelTemplate class="form-item" label="パスワード確認">
          <text-field v-model="passwordConfirm" required type="password" />
        </FormLabelTemplate>

        <div class="fullname">
          <FormLabelTemplate label="姓" class="lastname">
            <text-field
              v-model="lastName"
              placeholder="例）田中"
              :hide-details="false"
              required
            />
          </FormLabelTemplate>
          <FormLabelTemplate label="名" class="firstname">
            <text-field
              v-model="firstName"
              placeholder="例）太郎"
              :hide-details="false"
              required
            />
          </FormLabelTemplate>
        </div>
        <div class="fullname-notice">※氏名は登録後変更できません</div>
        <div class="fullname">
          <FormLabelTemplate class="lastname" label="姓:（last_name）カタカナ">
            <TextField
              v-model="last_name_kana"
              :hide-details="false"
              placeholder="例）タナカ"
              :rules="zenkakuKatakanaRules(last_name_kana)"
              required
            />
          </FormLabelTemplate>
          <FormLabelTemplate
            class="firstname"
            label="名:（first_name）カタカナ"
          >
            <TextField
              v-model="first_name_kana"
              :hide-details="false"
              placeholder="例）タロウ"
              :rules="zenkakuKatakanaRules(first_name_kana)"
              required
            />
          </FormLabelTemplate>
        </div>
        <div class="fullname">
          <FormLabelTemplate
            class="lastname"
            label="姓:（last_name）アルファベット"
          >
            <TextField
              v-model="last_name_alphabet"
              :hide-details="false"
              :rules="hankakuAlphabetRules(last_name_alphabet)"
              placeholder="例）Tanaka"
              required
            />
          </FormLabelTemplate>
          <FormLabelTemplate
            class="firstname"
            label="名:（first_name）アルファベット"
          >
            <TextField
              v-model="first_name_alphabet"
              :hide-details="false"
              :rules="hankakuAlphabetRules(first_name_alphabet)"
              placeholder="例）Taro"
              required
            />
          </FormLabelTemplate>
        </div>
        <FormLabelTemplate class="form-item" label="携帯電話番号">
          <TextField
            v-model="phone_number"
            :hide-details="false"
            :rules="phoneNumberRules(phone_number)"
            required
          />
        </FormLabelTemplate>
        <FormLabelTemplate class="form-item" label="生年月日">
          <div class="birthday">
            <SelectBox
              v-model="birthday.year"
              :hide-details="false"
              :items="yearItems"
              required
            />
            /
            <SelectBox
              v-model="birthday.month"
              :hide-details="false"
              :items="monthItems"
              required
            />
            /
            <SelectBox
              v-model="birthday.day"
              :hide-details="false"
              :items="dayItems"
              required
              :rules="dayRules"
            />
          </div>
        </FormLabelTemplate>
        <template v-if="isNeedParent">
          <FormLabelTemplate class="form-item" label="姓（保護者）">
            <TextField
              v-model="parent_last_name"
              :hide-details="false"
              required
            />
          </FormLabelTemplate>
          <FormLabelTemplate class="form-item" label="名（保護者）">
            <TextField
              v-model="parent_first_name"
              :hide-details="false"
              required
            />
          </FormLabelTemplate>
        </template>
        <FormLabelTemplate
          class="form-item"
          label="郵便番号（半角、ハイフンなし）"
        >
          <div class="zipcode">
            <TextField
              v-model="zipcode"
              :hide-details="false"
              :rules="zipCodeRules(zipcode)"
              required
              class="zipcode-input"
            />
            <CustomButton
              class="zipcode-button"
              height="36px"
              @click="handleClickSearchAddress"
              >住所検索</CustomButton
            >
          </div>
        </FormLabelTemplate>
        <FormLabelTemplate class="form-item" label="都道府県">
          <SelectBox
            v-model="prefecture"
            :hide-details="false"
            required
            :items="prefectureItems"
          />
        </FormLabelTemplate>
        <template v-if="!isSelectedAbroad">
          <FormLabelTemplate class="form-item" label="市区町村・町名">
            <TextField v-model="city" :hide-details="false" required />
          </FormLabelTemplate>
          <FormLabelTemplate class="form-item" label="上記以降の住所">
            <TextField v-model="banchi" :hide-details="false" required />
          </FormLabelTemplate>
        </template>
        <template v-else>
          <FormLabelTemplate class="form-item" label="住所">
            <TextField v-model="abroadAddress" :hide-details="false" required />
          </FormLabelTemplate>
        </template>
        <FormLabelTemplate class="form-item" label="性別">
          <SelectBox
            v-model="sex"
            :hide-details="false"
            required
            :items="sexItems"
          />
        </FormLabelTemplate>
        <FormLabelTemplate class="form-item" label="職業">
          <SelectBox
            v-model="job"
            :hide-details="false"
            required
            :items="jobItems"
          />
        </FormLabelTemplate>

        <div class="agreement">
          <label class="agreement-row">
            <checkbox v-model="isAgreePrivacyPolicy" />
            <div class="agreement-row-text">
              <a href="/privacy" target="_blank" rel="noopener"
                >プライパシーポリシー</a
              >に同意する
            </div>
          </label>
          <label class="agreement-row">
            <checkbox v-model="isAgreeTerms" />
            <div class="agreement-row-text">
              <a href="/terms" target="_blank" rel="noopener"
                >WithLIVE Meet&Greet 利用規約</a
              >に同意する
            </div>
          </label>
        </div>
        <custom-button @click="handleClickRegister">登録する</custom-button>
        <div class="link">
          <nuxt-link to="/login">すでに登録済みの方はこちら</nuxt-link>
        </div>
      </v-form>
      <template v-else>
        <div class="notice">
          入力したメールアドレスに送信される認証コードを入力してください。
        </div>
        <FormLabelTemplate class="form-item" label="認証コード">
          <text-field v-model="confirmationCode" />
        </FormLabelTemplate>
        <custom-button @click="handleClickConfirm">認証する</custom-button>
        <div class="link">
          <div class="link-resend" @click="handleClickResendCode">
            コードを再送信する
          </div>
        </div>
      </template>
    </div>
    <ActionConfirmModal
      :is-open.sync="isShowRegisterConfirmModal"
      text="一度登録したら、申請をしないと変更できません。よろしいですか？"
      @yes="handleClickRegisterConfirm"
      @no="isShowRegisterConfirmModal = false"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import config from '~/config'
import TextField from '~/components/atoms/TextField'
import FormLabelTemplate from '~/components/molcules/FormLabelTemplate'
import CustomButton from '~/components/atoms/Button'
import Checkbox from '~/components/atoms/Checkbox'
import SelectBox from '~/components/atoms/SelectBox'
import ActionConfirmModal from '~/components/organisms/ActionConfirmModal'
import { Gender, Job, Prefecture, Year, Month, Day } from '~/helpers/enum'
import {
  validateZenkakuKatakana,
  validateHankakuAlphabet,
  validatePhoneNumber,
  validateZipCode
} from '~/helpers/validator'

export default {
  middleware: ['setAccessToken', 'alreadyAuth'],
  components: {
    FormLabelTemplate,
    TextField,
    CustomButton,
    Checkbox,
    SelectBox,
    ActionConfirmModal
  },
  data: () => ({
    email: '',
    password: '',
    passwordConfirm: '',
    firstName: '',
    lastName: '',
    last_name_kana: '',
    first_name_kana: '',
    last_name_alphabet: '',
    first_name_alphabet: '',
    phone_number: '',
    zipcode: '',
    prefecture: '',
    city: '',
    banchi: '',
    abroadAddress: '',
    sex: '',
    job: '',
    birthday: {
      year: undefined,
      month: undefined,
      day: undefined
    },
    parent_first_name: '',
    parent_last_name: '',
    isAgreePrivacyPolicy: false,
    isAgreeTerms: false,
    isConfirming: false,
    confirmationCode: '',
    isShowRegisterConfirmModal: false
  }),
  computed: {
    sexItems: () => Gender.getList(),
    jobItems: () => Job,
    prefectureItems: () => Prefecture,
    yearItems: () => Year,
    monthItems: () => Month,
    dayItems: () => Day,
    birthdayDayjs() {
      const { year, month, day } = this.birthday
      if (!year || !month || !day) {
        return undefined
      }
      const dateStr = `${year}/${month}/${day}`
      const format = 'YYYY/M/D'

      return dayjs(dateStr, format)
    },
    dayRules() {
      const { year, month, day } = this.birthday
      if (!year || !month || !day) {
        return []
      }
      const dateStr = `${year}/${month}/${day}`
      const format = 'YYYY/M/D'

      const isValid = dayjs(dateStr, format).format(format) === dateStr
      return isValid ? [] : ['不正な日付です']
    },
    zenkakuKatakanaRules() {
      return (input) => {
        return [validateZenkakuKatakana(input) || true]
      }
    },
    hankakuAlphabetRules() {
      return (input) => {
        return [validateHankakuAlphabet(input) || true]
      }
    },
    phoneNumberRules() {
      return (input) => {
        return [validatePhoneNumber(input) || true]
      }
    },
    zipCodeRules() {
      return (input) => {
        return [validateZipCode(input) || true]
      }
    },
    isNeedParent() {
      if (!this.birthdayDayjs) {
        return false
      }

      const AGE = 15
      const diff = dayjs().diff(this.birthdayDayjs, 'year')
      return AGE > diff
    },
    isSelectedAbroad() {
      return this.prefecture === '海外'
    }
  },
  methods: {
    handleClickRegister() {
      const { email, password, passwordConfirm } = this
      if (!this.$refs.form.validate()) {
        this.$toast.error({
          message: '入力形式が無効な項目があります'
        })
        return
      }

      if (password !== passwordConfirm) {
        this.$store.dispatch('showErrorToast', {
          message: 'パスワードとパスワード確認が一致しません'
        })
        return
      }

      if (password.length < 6) {
        this.$store.dispatch('showErrorToast', {
          message: 'パスワードは6文字以上必要です'
        })
        return
      }

      if (!/[^\s]+@[^\s]+/.exec(email)) {
        this.$store.dispatch('showErrorToast', {
          message: 'メールアドレスの形式が正しくありません'
        })
        return
      }

      if (!this.isAgreePrivacyPolicy) {
        this.$store.dispatch('showErrorToast', {
          message: 'プライバシーポリシーに同意してください'
        })
        return
      }

      if (!this.isAgreeTerms) {
        this.$store.dispatch('showErrorToast', {
          message: 'WithLIVE Meet&Greet 利用規約に同意してください'
        })
        return
      }

      this.isShowRegisterConfirmModal = true
    },
    async handleClickRegisterConfirm() {
      try {
        this.$nuxt.$loading.start()

        await this.$authRepository.checkPhoneNumber(this.phone_number)

        const { year, month, day } = this.birthday
        const birthday = `${year}-${month}-${day}`

        const { email, password, firstName, lastName } = this

        await this.$authRepository.signup({
          email,
          lastName,
          firstName,
          password,
          lastNameKana: this.last_name_kana,
          firstNameKana: this.first_name_kana,
          lastNameAlphabet: this.last_name_alphabet,
          firstNameAlphabet: this.first_name_alphabet,
          phoneNumber: this.phone_number,
          zipcode: this.zipcode,
          prefecture: this.prefecture,
          city: this.isSelectedAbroad ? '海外' : this.city,
          banchi: this.isSelectedAbroad ? this.abroadAddress : this.banchi,
          sex: this.sex,
          job: this.job,
          birthday,
          parentLastName: this.parent_last_name,
          parentFirstName: this.parent_first_name,
          parent: this.isNeedParent
        })
        this.isShowRegisterConfirmModal = false
        this.isConfirming = true
      } catch (error) {
        if (error.code && error.code === 'UsernameExistsException') {
          this.$store.dispatch('showErrorToast', {
            message: 'このメールアドレスは登録済みです'
          })
        } else {
          this.noticeError(error)
          this.$toast.error({ error })
        }
      } finally {
        this.$nuxt.$loading.finish()
      }
    },
    async handleClickConfirm() {
      try {
        if (!this.confirmationCode.trim()) {
          return
        }
        this.$nuxt.$loading.start()
        const token = await this.$authRepository.verifyCodeAndSignin({
          email: this.email,
          code: this.confirmationCode,
          password: this.password
        })

        this.$store.commit('setAccessToken', token)
        this.$router.push(
          localStorage.getItem(config.redirectKey) || '/registerCodes'
        )
        localStorage.removeItem(config.redirectKey)
      } catch (error) {
        if (error.code && error.code === 'CodeMismatchException') {
          this.$store.dispatch('showErrorToast', {
            message: '認証コードが間違っています'
          })
        } else {
          this.noticeError(error)
          this.$store.dispatch('showErrorToast', {
            message:
              '予期せぬエラーが発生しました。リロードして再度お試しください。'
          })
        }
      } finally {
        this.$nuxt.$loading.finish()
      }
    },
    async handleClickResendCode() {
      try {
        this.$nuxt.$loading.start()
        await this.$authRepository.resendVerifyCode({
          email: this.email
        })
        this.$store.dispatch('showSuccessToast', {
          message: '認証コードを再送信しました'
        })
      } catch (error) {
        this.noticeError(error)
        this.$store.dispatch('showErrorToast', {
          message:
            '予期せぬエラーが発生しました。リロードして再度お試しください。'
        })
      } finally {
        this.$nuxt.$loading.finish()
      }
    },
    async handleClickSearchAddress() {
      this.$nuxt.$loading.start()
      try {
        const data = await this.$addressRepository.send({
          zipcode: this.zipcode
        })
        if (!data) {
          this.$toast.error({ message: '該当する住所が見つかりません' })
          return
        }
        this.prefecture = data.prefecture
        this.city = data.city
      } catch (error) {
        this.$toast.error({ error })
      } finally {
        this.$nuxt.$loading.finish()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.signup-page {
  padding: 16px 24px;
  max-width: 1040px;
  margin: 0 auto;
  color: #4a4a4a;
}
.signup-title {
  margin-bottom: 40px;
}
.form-item {
  margin-bottom: 24px;
}
.email-notice {
  font-size: 12px;
  text-align: right;
  margin-top: 8px;
}
.fullname {
  display: flex;
  justify-content: space-between;
}
.firstname {
  width: calc(50% - 8px);
}
.lastname {
  width: calc(50% - 8px);
}
.fullname-notice {
  font-size: 12px;
  text-align: right;
  margin: 8px 0 24px;
}
.notice {
  margin-bottom: 24px;
}
.agreement {
  margin-bottom: 24px;
  &-row {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    &-text {
      font-size: 14px;

      > a {
        margin-right: 4px;
      }
    }
  }
}
.link {
  margin-top: 16px;
  text-align: right;
  font-size: 14px;

  &-resend {
    cursor: pointer;
    color: #3dda84;
  }
}

.birthday {
  display: flex;
  align-items: center;
}

.zipcode {
  display: flex;
  align-items: center;

  &-button {
    width: 60px;
    margin-left: 16px;
  }
}
</style>
